import { gql } from '@graphql/__generated__';
import { REPORT_PREVIEW_ACTIONS_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import type { TourModule } from '@modules/product-updates/tours/tourTypes';

export const LISTING_BROCHURE_ANCHORS = {
  views: 'listing-brochure-views',
  buyerLink: 'listing-brochure-buyer-link',
  preview: 'listing-brochure-preview',
  actions: REPORT_PREVIEW_ACTIONS_ANCHOR,
} as const;

const GET_OWN_TOUR_LISTING = gql(/* GraphQL */ `
  query GetListingBrochureTourOwnListing($userId: uuid!) {
    lots(
      where: {
        broker_id: { _eq: $userId }
        status: {
          _in: [
            available
            available_coming_soon
            available_off_market
            reserved
            paused
            draft
          ]
        }
      }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
  }
`);

const GET_ANY_TOUR_LISTING = gql(/* GraphQL */ `
  query GetListingBrochureTourAnyListing {
    lots(
      where: {
        status: {
          _in: [available, available_coming_soon, available_off_market]
        }
      }
      order_by: [{ created_at: desc }]
      limit: 1
    ) {
      id
    }
  }
`);

// Starts the first time a user opens a listing's Brochure tab (see
// `ListingBrochure.tsx`), or from "Show me" on the release entry
// `content/releases/listing-brochure-tracking`.
export const listingBrochureTour: TourModule = {
  steps: t => [
    {
      anchor: LISTING_BROCHURE_ANCHORS.views,
      title: t('See who opens your brochure'),
      body: t(
        'Views and people over the last 30 days. Every brochure link you send from the CRM is tracked, including the enquiry auto-reply.',
      ),
      placement: 'right-start',
    },
    {
      anchor: LISTING_BROCHURE_ANCHORS.buyerLink,
      title: t('A personal link for each buyer'),
      body: t(
        'Contacting a buyer outside the CRM? Create their own link: you will see exactly when they open it.',
      ),
      placement: 'right',
    },
    {
      anchor: LISTING_BROCHURE_ANCHORS.preview,
      title: t('What buyers see'),
      body: t(
        'This is the brochure your buyers open. Its template and language are set on the left.',
      ),
      placement: 'left',
    },
    {
      anchor: LISTING_BROCHURE_ANCHORS.actions,
      title: t('Edit and share'),
      body: t(
        'Edit opens the full brochure editor. Share copies a tracked link to send anywhere, or downloads and prints the PDF.',
      ),
      placement: 'top',
    },
  ],
  // The user's latest listing, else the latest listing they can see.
  resolvePath: async ({ client, userId }) => {
    const own = await client.query({
      query: GET_OWN_TOUR_LISTING,
      variables: { userId },
      fetchPolicy: 'network-only',
    });
    const ownId = own.data?.lots[0]?.id;
    if (ownId != null) {
      return `/listings/${ownId}/brochure`;
    }
    const any = await client.query({
      query: GET_ANY_TOUR_LISTING,
      fetchPolicy: 'network-only',
    });
    const anyId = any.data?.lots[0]?.id;
    return anyId == null ? null : `/listings/${anyId}/brochure`;
  },
};
